
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button as RBButton,
  Alert,
  Spinner,
} from "react-bootstrap";
import { Box, Typography, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../hooks/useAuth";
import { loginThunk } from "../../services/auth/index";
import { RootState } from "../../store";
import { clearError } from "../../services/auth/authSlice";
import { useLocation, useNavigate } from "react-router-dom";
import CustomLogo from "../../assets/icons/customLogo";
import LeftSection from "./LeftSection";
import { LoginRequest } from "../../types";
import { VALIDATION } from "../../constants";

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token, loading, error } = useAppSelector((state: RootState) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<LoginRequest>({
    name: "",
    password: "",
    mobileNumber: "",
  });
  const [errors, setErrors] = useState<Partial<LoginRequest>>({});
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof LoginRequest]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginRequest> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Username is required";
    } else if (formData.name.length < VALIDATION.USERNAME_MIN_LENGTH) {
      newErrors.name = `Username must be at least ${VALIDATION.USERNAME_MIN_LENGTH} characters`;
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    dispatch(clearError());
    
    dispatch(loginThunk({ 
      name: formData.name, 
      mobileNumber: formData.mobileNumber, 
      password: formData.password 
    }));
  };

  useEffect(() => {
    if (token) {
      const from = (location.state as any)?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [token, navigate]);

  return (
    <Box sx={{ minHeight: "100vh", display: "flex" }}>
      <Container fluid className="p-0">
        <Row className="g-0 h-100">
          <LeftSection />
          <Col xs={12} md={4}>
            <Box sx={{ width: "100%", maxWidth: 450, marginLeft: { md: "-25px" }, px: { xs: 3, md: 0 } }}>
              <Box className="d-flex align-items-center justify-content-center mb-3" sx={{ mt: { xs: "50px", md: "100px" }, pb: "30px" }}>
                <CustomLogo width={40} height={35} style={{ marginRight: "12px" }} />
                <img src="/Nimbo.svg" alt="Nimbo" style={{ height: "45px", width: "auto" }} />
              </Box>

              <Typography variant="h2" className="text-center mb-4" sx={{
                color: "rgba(13, 42, 86, 0.15)",
                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
                fontWeight: "bold",
                pb: "20px",
                letterSpacing: "1px",
                textShadow: "0 0 8px rgba(13, 42, 86, 0.1)",
              }}>
                LOGIN
              </Typography>

              {error && (
                <Alert variant="danger" className="mb-3" dismissible onClose={() => dispatch(clearError())}>
                  <strong>Login Failed:</strong> {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: "rgba(116, 116, 116, 1)", fontWeight: 500 }}>Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    isInvalid={!!errors.name}
                    placeholder="Enter your username"
                    style={{ backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #e9ecef", padding: "12px 16px", fontSize: "16px", height: "50px" }}
                  />
                  {errors.name && <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>}
                </Form.Group>

                <Form.Group className="mb-5">
                  <Form.Label style={{ color: "rgba(116, 116, 116, 1)", fontWeight: 500 }}>Password</Form.Label>
                  <Box sx={{ position: "relative" }}>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      isInvalid={!!errors.password}
                      placeholder="Enter your password"
                      style={{ backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #e9ecef", padding: "12px 50px 12px 16px", fontSize: "16px", height: "50px" }}
                    />
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#6c757d" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Box>
                  {errors.password && <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>}
                </Form.Group>

                <RBButton
                  type="submit"
                  variant="primary"
                  className="w-100"
                  disabled={loading}
                  style={{
                    padding: "12px 24px",
                    fontSize: "16px",
                    fontWeight: 600,
                    borderRadius: "8px",
                    backgroundColor: "#F04E23",
                    borderColor: "#F04E23",
                    height: "50px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#d63e1f";
                    e.currentTarget.style.borderColor = "#d63e1f";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#F04E23";
                    e.currentTarget.style.borderColor = "#F04E23";
                  }}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : "LOGIN"}
                </RBButton>
              </Form>
            </Box>
          </Col>
        </Row>
      </Container>
    </Box>
  );
};

export default LoginPage;
